import { css, html, LitElement, PropertyValues } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import "./idea";
import "../atoms/tooltip";
import "./../atoms/icon";
import { property } from "lit/decorators/property.js";
import { FriendlyId, Id, Idea, isId } from "shared";
import { sharedStyles } from "../styles/shared";
import { ICON_IDEA } from "../../icons";
import { Store } from "../state/store";
import { ReplaySubject, switchMap } from "rxjs";
import { model$ } from "../state/model/model-list";
import { modelStore } from "../state/model/model-store";
import { Controller } from "../state/controller";
import { findFirstScrollableParent } from "../util/util";
import { roomStore } from "../state/room/room-store";
import { ifDefined } from "lit/directives/if-defined.js";
import { api } from "../api/api";
import { openViewIdea } from "../dialogs/open-view-idea";

/**
 * Replaces all ids in the text with the corresponding component.
 * `Heres somes ids: (risetnirsent-rsitenrisetn-rsitenrsi,riufnwypun-wfupmuwfmp-wfupmwufm) rest of the text more ids here: (risetnirsent-rsitenrisetn-rsitenrsi).`;
 * @param text
 * @param getComponent
 * @param transform
 */
export function replaceIdeaRefsInText({
	text,
	getComponent = (id) => `<bs-idea-tooltip idea-id="${id}"></bs-idea-tooltip>`,
	transform = (text) => text.join("")
}: {
	text: string;
	getComponent?: (id: string | FriendlyId) => string;
	transform?: (text: string[]) => string;
}) {
	return text.replaceAll(/\(?\[\^(.*?)]\)?/gim, (match, ids: string) =>
		transform(
			ids.split(",").map((v) => {
				// Clean up the id
				const id = v.replaceAll(/\D+/g, "").trim();

				const parsedFriendlyId = parseInt(id);
				if (isNaN(parsedFriendlyId)) return `[^${v}]`;

				return getComponent(parsedFriendlyId);
			})
		)
	);
}

class IdeaTooltipStore extends Store {
	input$ = {
		ideaId: new ReplaySubject<Id<Idea>>(1)
	};

	model$ = {
		idea: model$<Idea>([this.input$.ideaId]).pipe(switchMap((idea) => modelStore.get$<Idea>(idea))),
		currentRoom: roomStore.model$.currentRoom
	};
}

@customElement("bs-idea-tooltip")
export class BsIdeaTooltip extends LitElement {
	static get styles() {
		return [
			sharedStyles,
			css`
				:host {
					white-space: initial;
				}

				:host {
					--icon-size: 14px;
				}

				#trigger {
					cursor: pointer;
				}
			`
		];
	}

	private controller = new Controller(this, new IdeaTooltipStore());

	@property({ type: String, attribute: "idea-id" }) ideaId!: Id<Idea> | FriendlyId;
	@property({ type: Number }) truncate: number = 200;

	protected updated(props: PropertyValues) {
		super.updated(props);

		if (props.has("ideaId")) {
			if (isId(this.ideaId)) {
				this.controller.store.input$.ideaId.next(this.ideaId);
			}
		}
	}

	protected async onHover() {
		// On hover, ensure that the idea is loaded if "id" is a friendly id
		if (!isId(this.ideaId) && this.controller.model.idea == null) {
			// Get idea by friendly id

			const idea = await api.idea.get(Number(this.ideaId));
			console.log("idea", idea);

			this.controller.store.input$.ideaId.next(idea.id);
		}
	}

	render() {
		const { idea, currentRoom } = this.controller.model;

		return html`
			<bs-tooltip
				?inactive="${idea == null}"
				.contentTemplate="${() =>
					idea != null ? html`<bs-idea .idea="${idea}" truncate="${ifDefined(this.truncate)}"></bs-idea>` : undefined}"
				.getContainer="${() => findFirstScrollableParent(this) ?? window}"
				><bs-icon
					id="trigger"
					@mouseenter="${this.onHover}"
					@click="${() => (idea ? openViewIdea(idea) : undefined)}"
					.icon="${ICON_IDEA}"
					fill
				></bs-icon
			></bs-tooltip>
		`;
	}
}
